import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useContext, useEffect } from 'react'

import { accountAddressPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityAccountAddressPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext, getLinkPageUrl } from '@lib/routes'
import {
  defaultModuleQueryParameters,
  getAccountAddressPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { UserContext } from '@lib/user/context'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type AccountAddressPageProps = BasePageProps & {
  page: SanityAccountAddressPage
  site: SanitySiteFragment
}

const AccountAddressPage = ({
  draftMode,
  locale,
  page,
  site,
}: AccountAddressPageProps) => {
  const { user } = useContext(UserContext)

  const router = useRouter()

  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  // Redirect to login page if not logged in
  useEffect(() => {
    if (user && !user.isLoggedIn) {
      const url = getLinkPageUrl({
        pageType: 'loginPage',
      })
      router.push(url, url, {
        locale,
      })
    }
  }, [draftMode, locale, router, user])

  if (!user?.isLoggedIn) {
    return null
  }

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={accountAddressPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<AccountAddressPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const accountAddressPage = await getAccountAddressPage(
    sanityClient,
    context.locale,
  )

  if (!accountAddressPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(accountAddressPage.page),
      site: getNonNullValues(accountAddressPage.site),
    },
    revalidate: 60,
  }
}

export default AccountAddressPage
